import React, { useState, useEffect } from 'react';
import { Icon, CustomTable, Search, Pagination, Loader, ConfirmationPopup, SuccessModal, Tooltip } from '@ohif/ui';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import TeleServicePriceForm from './TeleServicePriceForm';
import getTeleServicePrice from '@custom/rtk-apis/Setting/getTeleServicePrice';
import deleteTeleServicePrice from '@custom/rtk-apis/Setting/deleteTeleServicePrice';
import addTeleServicePrice from '@custom/rtk-apis/Setting/addTeleServicePrice';
import getModalityStudy from '@custom/rtk-apis/modalityStudyRegion/getModalityStudy';
import getRadiologistList from '@custom/rtk-apis/billing/consultation/getRadiologistList';
import { clearGetTeleServicePriceState } from '@custom/store/slices/Setting/getTeleServicePriceSlice';
import { clearAddTeleServicePriceState } from '@custom/store/slices/Setting/addTeleServicePriceSlice';
import { clearDeleteTeleServicePriceState } from '@custom/store/slices/Setting/deleteTeleServicePriceSlice';
import editTeleServicePrice from '@custom/rtk-apis/Setting/editTeleServicePrice';
import { clearEditTeleServicePriceState } from '@custom/store/slices/Setting/editTeleServicePriceSlice';
import { useTheme } from "../../../ui/src/contextProviders";
import { getCurrencySymbol } from '@custom/Utils/utils';
import { customStyle } from '@custom/Utils/utils';
import Header from '../../Header/Header';


const TeleServicePrice = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [tableData, setTableData] = useState([]);
  const [pgNumber, setPgNumber] = useState(1);
  const [pgLimit, setPgLimit] = useState(30);
  const [totalPg, setTotalPg] = useState(10);
  const [search, setSearch] = useState('');
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [editConsultation, setEditConsultation] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [success, setSuccess] = useState(false);
  const [msg, setMsg] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [edit, setEdit] = useState(false);
  const [modalities, setModalities] = useState([]);
  const [radiologistNames, setRadiologistNames] = useState([]);
  const [existingRadiologists, setExistingRadiologists] = useState([]);
  const [studyRegions, setStudyRegions] = useState([]);
  const isDarkMode = useTheme();

  const teleServicePriceData = useSelector((state: RootState) => state.getTeleServicePriceSliceResponse);
  const addTeleServicePriceData = useSelector((state: RootState) => state.addTeleServicePriceSliceResponse);
  const deleteTeleServicePriceData = useSelector((state: RootState) => state.deleteTeleServicePriceSliceResponse);
  const editTeleServicePriceData = useSelector((state: RootState) => state.editTeleServicePriceSliceResponse);
  const modalityAndStudyRegion = useSelector((state: RootState) => state.getModalityStudySliceResponse);
  const radiologistList = useSelector((state: RootState) => state.getRadiologistListResponse);

  const columns = [
    { key: 'doctorName', title: 'Doctor Name' },
    { key: 'modality', title: 'Modality' },
    { key: 'studyRegion', title: 'Study Region' },
    { key: 'price', title: 'Price', sort: true },
    { key: 'actions', title: 'Action' },
  ];

  const defaultInitialValues = {
    doctorId: '',
    doctorName: '',
    services: [
      {
        modality: '',
        studyRegion: '',
        price: ''
      }
    ]
  };

  const [initialValues, setInitialValues] = useState(defaultInitialValues);

  const onPageChangeHandler = (pg) => {
    setPgNumber(pg);
  };

  const handlePageSizeChange = (e) => {
    setPgLimit(e);
  };

  const submitForm = async (values) => {
    if (edit && editConsultation) {
      const payload = {
        _id: editConsultation._id || '',
        ...values
      };
      dispatch(editTeleServicePrice(payload));
    } else {
      const payload = {
        ...values
      };
      dispatch(addTeleServicePrice(payload));
    }
  };

  const handleAddCharges = () => {
    dispatch(getModalityStudy());
    dispatch(getRadiologistList());
    setEditConsultation(null);
    setEdit(false);
    setInitialValues(defaultInitialValues);
    setShowConsultationForm(true);
  };

  const handleDelete = async () => {
    setIsPopupOpen(false);
    if (selectedRow) {
      setIsDeleting(true);
      const payload = {
        _id: selectedRow?.rawData?._id || ""
      };
      dispatch(deleteTeleServicePrice(payload));
    }
  };

  const handleCancel = () => {
    setIsPopupOpen(false);
  };

  const capitalizeWords = (str) =>
    str
      ? str
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ')
      : '';

  const actions = [
    {
      id: 'editConsultation',
      label: 'Edit',
      iconName: 'edit',
      onClick: (item) => {
        console.log(item);
        setEdit(true);
        setEditConsultation(item);
        setInitialValues(item.rawData);
        dispatch(getModalityStudy());
        dispatch(getRadiologistList());
        setShowConsultationForm(true);
      },
    },
    {
      id: 'deleteConsultation',
      label: 'Delete',
      iconName: 'delete',
      onClick: (item) => {
        setSelectedRow(item);
        setIsPopupOpen(true);
      },
    },
  ];

  const searchClick = (searchText) => {
    setSearch(searchText);
    setSearchQuery(searchText);
    setSearchInputValue(searchText);
  };

  useEffect(() => {
    if (deleteTeleServicePriceData.status === 'SUCCESS' || deleteTeleServicePriceData.status === 'success') {
      setMsg('Service price deleted successfully');
      setSuccess(true);
      setIsDeleting(false);
      setIsPopupOpen(false);
      setTimeout(() => {
        dispatch(getTeleServicePrice({ limit: pgLimit, pageNumber: pgNumber, search: searchQuery }));
      }, 500);
    }
    // Only clear on unmount, not on every change
    return () => {};
  }, [deleteTeleServicePriceData.status]);

  // Separate cleanup effect
  useEffect(() => {
    return () => {
      dispatch(clearDeleteTeleServicePriceState());
    };
  }, []);

  useEffect(() => {
    dispatch(getTeleServicePrice({ limit: pgLimit, pageNumber: pgNumber, search: searchQuery }));
  }, [dispatch, searchQuery, pgLimit, pgNumber]);

  useEffect(() => {
    if ((teleServicePriceData.status === 'SUCCESS' || teleServicePriceData.status === 'success') && teleServicePriceData.result.data) {
      // Process the API response to flatten services for display
      let formattedData = [];

      if (Array.isArray(teleServicePriceData.result.data)) {
        teleServicePriceData.result.data.forEach(doctor => {
          if (doctor.services && Array.isArray(doctor.services) && doctor.services.length > 0) {
            // Group services by doctor
            const allModalitiesText = doctor.services.map(s => s.modality).join(', ');
            const allStudyRegionsText = doctor.services.map(s => s.studyRegion).join(', ');
            const allPriceText = doctor.services.map(s => s.price).join(', ');

            // Create a row entry for the first service with doctor info
            const firstService = doctor.services[0];

            // Format modality display - truncate if multiple
            const modalityDisplay = doctor.services.length > 1
              ? (
                  <Tooltip content={allModalitiesText} position='bottom-left'>
                    {`${firstService.modality}...`}
                  </Tooltip>
                )
              : firstService.modality;

            // Format study region display - truncate if multiple
            const studyRegionDisplay = doctor.services.length > 1
              ? (
                  <Tooltip content={allStudyRegionsText} position='bottom-left'>
                    {`${firstService.studyRegion}...`}
                  </Tooltip>
                )
              : firstService.studyRegion;

            const priceWithRupee = doctor.services.length > 1 ? (
              <Tooltip content={allPriceText} position='bottom-left'>
                {`₹ ${firstService.price}...`}
              </Tooltip>
            ) : `₹ ${firstService.price}`;
            formattedData.push({
              _id: firstService._id || doctor._id,
              doctorId: doctor.doctorId,
              doctorName: capitalizeWords(doctor.doctorName),
              modality: modalityDisplay,
              studyRegion: studyRegionDisplay,
              price: priceWithRupee,
              // Store original data for edit operations
              rawData: doctor
            });
          }
        });
      }

      setTotalPg(teleServicePriceData.result.totalCount);
      setTableData(formattedData);
    }
  }, [teleServicePriceData.status, teleServicePriceData.result.data]);

  // Separate cleanup effect
  useEffect(() => {
    return () => {
      dispatch(clearGetTeleServicePriceState());
    };
  }, []);

  useEffect(() => {
    if (modalityAndStudyRegion.status === 'SUCCESS' && modalityAndStudyRegion.result) {
      setModalities(modalityAndStudyRegion.result.modalities);
      setStudyRegions(modalityAndStudyRegion.result.studyRegions);
    }
  }, [modalityAndStudyRegion.status, modalityAndStudyRegion.result]);

  useEffect(() => {
    if (radiologistList?.status === 'SUCCESS') {
      setRadiologistNames(radiologistList?.result);
    }
  }, [radiologistList.status]);

  useEffect(() => {
    const existingRadiologistIds = tableData?.map(
      item => item?.doctorId
    );

    setExistingRadiologists(existingRadiologistIds);

  }, [tableData]);

  useEffect(() => {
    if (addTeleServicePriceData?.status === 'SUCCESS' || addTeleServicePriceData?.status === 'success') {
      setSuccess(true);
      setMsg('Service price added successfully.');
      setTimeout(() => {
        setShowConsultationForm(false);
        dispatch(getTeleServicePrice({ limit: pgLimit, pageNumber: pgNumber, search: searchQuery }));
        dispatch(clearAddTeleServicePriceState());
      }, 500);
    }
  }, [addTeleServicePriceData.status]);

  useEffect(() => {
    if (editTeleServicePriceData?.status === 'SUCCESS' || editTeleServicePriceData?.status === 'success') {
      setSuccess(true);
      setMsg('Service price updated successfully.');
      setTimeout(() => {
        setShowConsultationForm(false);
        dispatch(getTeleServicePrice({ limit: pgLimit, pageNumber: pgNumber, search: searchQuery }));
        dispatch(clearEditTeleServicePriceState());
      }, 500);
    }
  }, [editTeleServicePriceData?.status]);

  return (
    <>
    <div className='page page__fullBody' style={
                    isDarkMode?.isDarkMode ? { background: '#090c29' } : customStyle.mainBackground
                }>
                    <Header isBack={false} title='Tele Service Pricing' />
      <div className="page__body">
        <div className="tableWrap">
          {!showConsultationForm ? (
            <>
              <div className="tableWrap__head">
                <div className="formRow formRow__2 m_0 w_100 formRow--flexEnd">
                  <div className="formCol">
                    <Search
                      text="Doctor Name"
                      onSearchClick={searchClick}
                      value={searchInputValue}
                      setValue={setSearchInputValue}
                    />
                  </div>
                </div>

                  <div className="btn__group">
                    <button className="btn btn__white" onClick={handleAddCharges}>
                      <svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M7.63509 1.16699V12.8337M1.80176 7.00033H13.4684"
                          stroke="#150823"
                          strokeWidth="1.67"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      Add Prices
                    </button>
                  </div>

              </div>
              <div className="tableContainer">
                <CustomTable
                  columns={columns}
                  data={tableData}
                  actions={actions}
                  searchQuery={searchQuery}
                  from={'teleserviceprice'}
                  userDetails={{}}
                  setClickedItem={() => {}}
                  clickedItem={null}
                />
              </div>
            </>
          ) : (
            <TeleServicePriceForm
              editConsultation={editConsultation}
              submitForm={submitForm}
              edit={edit}
              setEdit={setEdit}
              modalities={modalities}
              studyRegions={studyRegions}
              radiologistNames={radiologistNames}
              initialValues={initialValues}
              setShowConsultationForm={setShowConsultationForm}
              isSubmitting={addTeleServicePriceData.loading || editTeleServicePriceData.loading}
              existingRadiologists={existingRadiologists}
            />
          )}
        </div>
      </div>
      <SuccessModal
        isSuccessModalOpen={success}
        title={msg}
        handleCloseModal={() => setSuccess(false)}
      />
      <ConfirmationPopup
        isOpen={isPopupOpen}
        message={`Are you sure you want to delete this service price for ${selectedRow?.doctorName}?`}
        onConfirm={handleDelete}
        onCancel={handleCancel}
        title={''}
      />
      <Loader isLoaderOpen={addTeleServicePriceData.loading || teleServicePriceData.loading || editTeleServicePriceData.loading || deleteTeleServicePriceData.loading} />
      {tableData.length > 0 && !showConsultationForm && (
        <Pagination
          onChangePage={onPageChangeHandler}
          onChangePerPage={handlePageSizeChange}
          totalPage={totalPg}
          currentPage={pgNumber}
          perPage={pgLimit}
        />
      )}
    </div>
    </>
  );
};

export default TeleServicePrice;
